import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { KeyRound, Lock, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ResetPasswordPage = () => {
  usePageMeta({
    title: 'Reset Password',
    description: 'Set a new password for your Digitive Health account.',
    path: '/reset-password',
    noindex: true,
  });

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Reset failed');
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background" data-testid="reset-password-page">
        <Header />
        <section className="pt-28 pb-16">
          <div className="container-width max-w-md">
            <Card className="p-8 text-center border-border/50">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h1 className="text-xl font-bold text-foreground mb-2">Invalid Reset Link</h1>
              <p className="text-sm text-muted-foreground mb-6">This password reset link is invalid or has expired.</p>
              <Link to="/auth">
                <Button className="bg-primary text-white rounded-lg" data-testid="back-to-login-btn">Back to Sign In</Button>
              </Link>
            </Card>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="reset-password-page">
      <Header />
      <section className="pt-28 pb-16">
        <div className="container-width max-w-md">
          <Card className="p-6 md:p-8 border-border/50" data-testid="reset-password-card">
            {success ? (
              <div className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h1 className="text-2xl font-bold text-foreground tracking-tight mb-2">Password Reset</h1>
                <p className="text-sm text-muted-foreground mb-6">Your password has been updated successfully. You can now sign in with your new password.</p>
                <Link to="/auth">
                  <Button className="bg-primary text-white rounded-lg w-full h-11" data-testid="go-to-login-btn">Sign In</Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <KeyRound className="h-6 w-6 text-primary" />
                  </div>
                  <h1 className="text-2xl font-bold text-foreground tracking-tight">Set New Password</h1>
                  <p className="text-sm text-muted-foreground mt-1">Enter your new password below.</p>
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-sm" data-testid="reset-error">
                    <AlertCircle className="h-4 w-4 shrink-0" />{error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="Min 6 characters" className="pl-10 h-10" data-testid="new-password-input" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">Confirm Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required placeholder="Repeat password" className="pl-10 h-10" data-testid="confirm-password-input" />
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-primary text-white h-11 rounded-lg" data-testid="reset-submit-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reset Password'}
                  </Button>
                </form>
              </>
            )}
          </Card>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default ResetPasswordPage;
